import React, { useEffect } from "react";
import { Form, FormInstance, Input, theme, Typography } from "antd";
import { IPartnerDeal } from "../../../../../interfaces/partnerDeal.interface";
import PartnerSelector from "../../partner-selector/PartnerSelector";
import { WizardMode } from "../dealWizardSteps";

const { Text } = Typography;

interface MerchantDetailsStepProps {
  form: FormInstance;
  mode: WizardMode;
  details?: Partial<IPartnerDeal>;
  showPartnerSelect: boolean;
  selectedPartnerId?: string;
  onSelectPartner: (partnerId: string) => void;
}

export default function MerchantDetailsStep({
  form,
  mode,
  details,
  showPartnerSelect,
  selectedPartnerId,
  onSelectPartner,
}: MerchantDetailsStepProps) {
  useEffect(() => {
    form.setFieldsValue({
      businessName: details?.businessName,
      businessRegistrationNumber: details?.businessRegistrationNumber,
    });
  }, [details, form]);

  if (mode === "edit") {
    return <ReadView details={details} partnerVisible={showPartnerSelect} />;
  }

  return (
    <Form form={form} layout="vertical" requiredMark autoComplete="off">
      {showPartnerSelect && (
        <Form.Item
          label="Partner"
          required
          style={{ marginBottom: 4 }}
          help="Pick the partner this deal belongs to."
        >
          <PartnerSelector
            onChange={onSelectPartner}
            value={selectedPartnerId}
            allowClear={false}
            width="50%"
          />
        </Form.Item>
      )}

      <Form.Item
        name="businessName"
        label="Merchant Name"
        style={{ marginTop: showPartnerSelect ? 20 : 0 }}
        rules={[{ required: true, message: "Please add a merchant name." }]}
      >
        <Input
          placeholder="e.g. De Goudvis Restaurant B.V."
          style={{ width: "50%" }}
        />
      </Form.Item>

      <Form.Item
        name="businessRegistrationNumber"
        label="KVK"
        rules={[
          { required: true, message: "Please add a KVK number." },
          {
            pattern: /^\d{8}$/,
            message: "Please put a valid number, equal to 8 digits.",
          },
        ]}
      >
        <Input
          placeholder="e.g. 87654321"
          minLength={8}
          maxLength={8}
          style={{ width: "50%" }}
        />
      </Form.Item>
    </Form>
  );
}

function ReadView({
  details,
  partnerVisible,
}: {
  details?: Partial<IPartnerDeal>;
  partnerVisible: boolean;
}) {
  const { token } = theme.useToken();

  const rows: { label: string; value: React.ReactNode }[] = [
    ...(partnerVisible
      ? [{ label: "Partner", value: details?.partnerName || "—" }]
      : []),
    { label: "Merchant name", value: details?.businessName || "—" },
    { label: "KVK", value: details?.businessRegistrationNumber || "—" },
  ];

  return (
    <div>
      {rows.map((row, index) => (
        <div
          key={row.label}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            padding: "12px 0",
            borderBottom:
              index < rows.length - 1
                ? `1px solid ${token.colorBorderSecondary}`
                : "none",
          }}
        >
          <Text
            type="secondary"
            style={{
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: 0.4,
            }}
          >
            {row.label}
          </Text>
          <span style={{ fontWeight: 500 }}>{row.value}</span>
        </div>
      ))}
    </div>
  );
}
